import { LAWYER_INFO } from '@/pages/lawyer/model/keys';
import { logError, normalizeError } from '@/shared/lib/errors';
import {
  LAWYER_ARTICLES_PER_PAGE,
  COMPANY_PUBLICATIONS_PER_PAGE,
  ARTICLES_LOAD_ERROR,
  PUBLICATIONS_LOAD_ERROR,
} from '@/pages/lawyer/lib/publicationsConstants';
import type { IJournalArticle } from '@/entities/journal/model/types';
import type { IPublication } from '@/entities/publication/model/types/publication';
import type { ApiResponse } from '@/shared/model/types/http';
import type {
  PublicationsItem,
  PublicationsPage,
} from '@/pages/lawyer/model/types/publicationsBlock';

const toPage = <T extends PublicationsItem>(
  response: ApiResponse<T[]>,
): PublicationsPage<T> => ({
  items: Array.isArray(response.data) ? response.data : [],
  total: response.meta?.total ?? 0,
});

export const useLawyerPublications = (isCompany: boolean) => {
  const lawyerInfo = inject(LAWYER_INFO);
  const { $journalService, $publicationService } = useNuxtApp();
  const isMobile: boolean = useIsMobile();

  const perPage = isCompany ? COMPANY_PUBLICATIONS_PER_PAGE : LAWYER_ARTICLES_PER_PAGE;
  const userId: ComputedRef<number> = computed(() => lawyerInfo?.value?.user_id ?? 0);

  const currentPage: Ref<number> = ref(1);
  const isPageLoading: Ref<boolean> = ref(false);
  const extraArticles: Ref<IJournalArticle[]> = ref([]);
  const extraPublications: Ref<IPublication[]> = ref([]);

  const emptyPage = <T extends PublicationsItem>(): PublicationsPage<T> => ({
    items: [],
    total: 0,
  });

  const fetchArticlesPage = async (
    page: number,
  ): Promise<PublicationsPage<IJournalArticle>> => {
    const response = await $journalService.getArticles<IJournalArticle[]>({
      user_id: userId.value,
      per_page: perPage,
      page,
      'sort-created_at': 'desc',
    });
    return toPage(response);
  };

  const fetchPublicationsPage = async (
    page: number,
  ): Promise<PublicationsPage<IPublication>> => {
    const response = await $publicationService.getPublications({
      user_id: userId.value,
      per_page: perPage,
      page,
    });
    return toPage(response);
  };

  const fetchFirstPage = async (): Promise<PublicationsPage<PublicationsItem>> => {
    if (!userId.value) return emptyPage();
    try {
      return isCompany ? await fetchPublicationsPage(1) : await fetchArticlesPage(1);
    } catch (error) {
      logError(normalizeError(error), {
        action: isCompany ? PUBLICATIONS_LOAD_ERROR : ARTICLES_LOAD_ERROR,
      });
      return emptyPage();
    }
  };

  const { data: firstPage, pending: isFirstPageLoading } = useAsyncData<
    PublicationsPage<PublicationsItem>
  >(
    () =>
      `lawyer-publications:${isCompany ? 'company' : 'lawyer'}:${userId.value || 'none'}`,
    fetchFirstPage,
    {
      default: emptyPage,
      watch: [userId],
      getCachedData: (key, nuxtApp, ctx) =>
        ctx.cause === 'initial' ? nuxtApp.payload.data[key] : undefined,
    },
  );

  const isArticle = (item: PublicationsItem): item is IJournalArticle =>
    'content' in item;
  const isPublication = (item: PublicationsItem): item is IPublication => 'url' in item;

  const firstArticles: ComputedRef<IJournalArticle[]> = computed(() =>
    firstPage.value.items.filter(isArticle),
  );
  const firstPublications: ComputedRef<IPublication[]> = computed(() =>
    firstPage.value.items.filter(isPublication),
  );

  const totalItems: ComputedRef<number> = computed(() => firstPage.value.total);

  const articles: ComputedRef<IJournalArticle[]> = computed(() =>
    currentPage.value === 1
      ? firstArticles.value
      : isMobile
        ? [...firstArticles.value, ...extraArticles.value]
        : extraArticles.value,
  );

  const publications: ComputedRef<IPublication[]> = computed(() =>
    currentPage.value === 1
      ? firstPublications.value
      : isMobile
        ? [...firstPublications.value, ...extraPublications.value]
        : extraPublications.value,
  );

  const loadedCount: ComputedRef<number> = computed(() =>
    isCompany ? publications.value.length : articles.value.length,
  );

  const isShowPagination: ComputedRef<boolean> = computed(
    () => !isMobile && totalItems.value > perPage,
  );
  const isShowLoadMore: ComputedRef<boolean> = computed(
    () => isMobile && loadedCount.value < totalItems.value,
  );

  const loadPage = async (page: number): Promise<void> => {
    isPageLoading.value = true;
    try {
      if (isCompany) {
        const { items } = await fetchPublicationsPage(page);
        extraPublications.value = isMobile
          ? [...extraPublications.value, ...items]
          : items;
      } else {
        const { items } = await fetchArticlesPage(page);
        extraArticles.value = isMobile ? [...extraArticles.value, ...items] : items;
      }
      currentPage.value = page;
    } catch (error) {
      logError(normalizeError(error), {
        action: isCompany ? PUBLICATIONS_LOAD_ERROR : ARTICLES_LOAD_ERROR,
      });
    } finally {
      isPageLoading.value = false;
    }
  };

  const changePage = (page: number): void => {
    if (page === currentPage.value) return;
    if (page === 1) {
      currentPage.value = 1;
      return;
    }
    loadPage(page);
  };

  const loadMore = (): void => {
    loadPage(currentPage.value + 1);
  };

  const isLoading: ComputedRef<boolean> = computed(
    () => isFirstPageLoading.value || isPageLoading.value,
  );

  return {
    articles,
    publications,
    totalItems,
    currentPage,
    perPage,
    isLoading,
    isShowPagination,
    isShowLoadMore,
    changePage,
    loadMore,
  };
};

export default useLawyerPublications;
